/**
 * Shared plumbing for the NLNG SSO popup flow.
 *
 * The login page opens a small, screen-centered popup at the CAS login URL
 * instead of navigating the whole tab away. After Azure AD sign-in, CAS
 * redirects the popup to `/sso/callback?ticket=...&state=...`; that page hands
 * the ticket back to the opener (which owns the auth context) and closes
 * itself. The opener then exchanges the ticket for the LMS JWT exactly as
 * before — the ticket is single-use and only the backend can validate it.
 *
 * Two delivery channels are used, because `window.opener` is not guaranteed:
 *   1. `postMessage` to `window.opener` — the normal path.
 *   2. A same-origin `BroadcastChannel` — survives a severed opener (e.g. a
 *      `Cross-Origin-Opener-Policy: same-origin` response on the way back).
 * Whichever arrives first wins; the listener de-duplicates.
 *
 * See docs/SSO_INTEGRATION.md.
 */

export const SSO_POPUP_NAME = 'nlngSsoPopup';
export const SSO_CHANNEL_NAME = 'nlng-sso';
export const SSO_MESSAGE_TYPE = 'nlng-sso-result';

const SSO_FLOW_KEY = 'nlng-sso-popup-flow';
// The CAS state JWT lives 10 minutes; a popup attempt older than that is stale.
const SSO_FLOW_TTL_MS = 10 * 60 * 1000;

const POPUP_WIDTH = 520;
const POPUP_HEIGHT = 680;

/**
 * Flag the popup flow as in progress. Read by the callback page to decide
 * whether it is running inside a popup (handing the ticket back) or as a
 * top-level page (doing the exchange itself). localStorage is used rather than
 * `window.opener`/`window.name` because both can be lost across the
 * cross-origin CAS round trip.
 */
export const markSsoPopupFlow = () => {
  try {
    localStorage.setItem(SSO_FLOW_KEY, String(Date.now()));
  } catch (err) {
    // Storage disabled — the callback simply falls back to the redirect flow.
  }
};

export const clearSsoPopupFlow = () => {
  try {
    localStorage.removeItem(SSO_FLOW_KEY);
  } catch (err) {
    /* no-op */
  }
};

/** True when a popup sign-in was started from this browser in the last 10 min. */
export const isSsoPopupFlow = () => {
  try {
    const startedAt = Number(localStorage.getItem(SSO_FLOW_KEY));
    if (!startedAt) return false;
    return Date.now() - startedAt < SSO_FLOW_TTL_MS;
  } catch (err) {
    return false;
  }
};

/**
 * `window.open` features for a popup centered on the screen the app is on
 * (uses screenLeft/screenTop so it lands on the right monitor in dual-screen
 * setups, and clamps to the available screen size on small displays).
 */
export const buildCenteredPopupFeatures = () => {
  const screenLeft = window.screenLeft !== undefined ? window.screenLeft : window.screenX;
  const screenTop = window.screenTop !== undefined ? window.screenTop : window.screenY;
  const viewportWidth = window.innerWidth || document.documentElement.clientWidth || window.screen.width;
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight || window.screen.height;

  const width = Math.min(POPUP_WIDTH, window.screen.availWidth || POPUP_WIDTH);
  const height = Math.min(POPUP_HEIGHT, window.screen.availHeight || POPUP_HEIGHT);
  const left = Math.max(0, Math.round(screenLeft + (viewportWidth - width) / 2));
  const top = Math.max(0, Math.round(screenTop + (viewportHeight - height) / 2));

  return `popup=yes,width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`;
};

/**
 * Send the callback result from the popup back to the login tab.
 * @param {{ticket?: string, state?: string, error?: string}} payload
 */
export const postSsoResult = (payload) => {
  const message = { type: SSO_MESSAGE_TYPE, ...payload };

  try {
    if (window.opener && window.opener !== window) {
      window.opener.postMessage(message, window.location.origin);
    }
  } catch (err) {
    /* opener unavailable — the BroadcastChannel below still delivers */
  }

  try {
    if (typeof BroadcastChannel !== 'undefined') {
      const channel = new BroadcastChannel(SSO_CHANNEL_NAME);
      channel.postMessage(message);
      channel.close();
    }
  } catch (err) {
    /* no-op */
  }
};

/**
 * Listen for the popup's result on both channels.
 * @param {(payload: {ticket?: string, state?: string, error?: string}) => void} onResult
 * @returns {() => void} unsubscribe
 */
export const subscribeToSsoResult = (onResult) => {
  const handleMessage = (event) => {
    // Only trust messages this app posted from its own origin.
    if (event.origin !== window.location.origin) return;
    if (!event.data || event.data.type !== SSO_MESSAGE_TYPE) return;
    onResult(event.data);
  };

  window.addEventListener('message', handleMessage);

  let channel = null;
  try {
    if (typeof BroadcastChannel !== 'undefined') {
      channel = new BroadcastChannel(SSO_CHANNEL_NAME);
      channel.onmessage = (event) => {
        if (!event.data || event.data.type !== SSO_MESSAGE_TYPE) return;
        onResult(event.data);
      };
    }
  } catch (err) {
    channel = null;
  }

  return () => {
    window.removeEventListener('message', handleMessage);
    if (channel) {
      channel.onmessage = null;
      channel.close();
    }
  };
};
